import React, { useEffect, useRef, useState } from 'react';
import usePoiUpdateHandler from '../../components/pointsOfInterest/usePoiUpdateHandler';
import { Label } from '../../../common/labels/labelTypes';
import { TagEditable } from '@milanote/roma';
import { List } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import { getElementIdForSelectedLabel, getSelectedLabelIdForElement } from './labelSelectors';
import { selectLabels } from '../../../common/labels/labelActions';
import { isFeatureEnabled } from '../../../common/users/utils/userPropertyUtils';
import { ExperimentId } from '../../../common/experiments/experimentsConstants';
import { getCurrentUser } from '../../user/currentUserSelector';
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { cx } from '@milanote/roma/utils/cx';
import { canWrite } from '../../../common/permissions/permissionUtil';
import { getSelectedElementIds } from '../selection/selectedElementsSelector';
import { updateLabel } from '../../../common/labels/labelActions';
import { LABEL_NAME_MAX_LENGTH } from '../../../common/labels/labelConstants';
import { useLabelSelection } from './hooks/useLabelSelectionHooks';
import { useSelectedLabelDocumentEvents } from './hooks/useSelectedLabelDocumentEventsHooks';

type LabelElementPillsContainerProps = {
    labels: List<Label>;
    element: ImMNElement;
    selectedElementId?: string;
    size: 'xs' | 'sm' | 'md' | 'lg' | undefined;
    permissions: number;
    filterQuery?: string;
};

const SEARCH_HIGHLIGHT_CLASS = 'search-highlight';

/**
 * Refreshes label search highlights when the active filter changes.
 */
const useSearchHighlightUpdates = (onHighlightsChanged: () => void, filterQuery: string | undefined) => {
    // Refresh POI highlights when the label filter changes.
    useEffect(() => {
        onHighlightsChanged();
    }, [filterQuery, onHighlightsChanged]);
};

const LabelElementPillsContainer: React.FC<LabelElementPillsContainerProps> = ({
    labels,
    filterQuery,
    size,
    element,
    selectedElementId,
    permissions,
}) => {
    const dispatch = useDispatch();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isEditingLabelName, setIsEditingLabelName] = useState<boolean>(false);

    const elementId = getElementId(element);
    const selectableElementId = selectedElementId || elementId;
    const selectedLabelId = useSelector(getSelectedLabelIdForElement(elementId));
    const selectedLabelsElementId = useSelector(getElementIdForSelectedLabel);
    const currentUser = useSelector(getCurrentUser);
    const selectedElementIds = useSelector(getSelectedElementIds);

    const selectedKeys = selectedLabelId ? new Set<string>([selectedLabelId]) : new Set<string>();
    const isElementSingleSelected = selectedElementIds.size === 1 && selectedElementIds.includes(selectableElementId);
    const areLabelsEnabled = isFeatureEnabled(ExperimentId.tags, currentUser);
    const hasWritePermission = canWrite(permissions);
    const canSelectLabels = hasWritePermission && isElementSingleSelected;
    const isSelectedLabelInThisContainer = selectedLabelsElementId === selectableElementId && !!selectedLabelId;

    const handleLabelSelection = (event: React.MouseEvent, labelId: string) => {
        if (!canSelectLabels || !areLabelsEnabled) return;

        dispatch(selectLabels(labelId, elementId, selectableElementId));
    };

    const handleLabelRename = (labelId: string, name: string) => {
        if (!canSelectLabels || !areLabelsEnabled) return;

        dispatch(updateLabel(labelId, { name }));
    };

    const onHighlightsChanged = usePoiUpdateHandler({
        contentInnerRef: containerRef,
        elementId,
        selector: `.${SEARCH_HIGHLIGHT_CLASS}`,
    });
    useSearchHighlightUpdates(onHighlightsChanged, filterQuery);

    useLabelSelection({
        labels,
        selectedLabelId,
        isElementSingleSelected,
        isSelectedLabelInThisContainer,
    });

    useSelectedLabelDocumentEvents({
        containerRef,
        selectedLabelId,
        selectedLabelsElementId,
        selectableElementId,
        canSelectLabels,
        element,
        selectedKeys,
        isSelectedLabelInThisContainer,
        isEditingLabelName,
    });

    if (!labels?.size) return null;

    return (
        <div className="LabelElementPillsContainer mb-1 mr-1" ref={containerRef}>
            <div className="flex flex-wrap gap-1">
                {labels.map((label) => {
                    if (!label) return null;

                    const isSearchResult = filterQuery && label.name.toLowerCase().includes(filterQuery.toLowerCase());
                    const isSelected = selectedLabelId === label.id;

                    const tagBaseClassName = cx(
                        'inline-flex w-auto max-w-full transition-none',
                        isSelected && 'ring-2 ring-primary_focused',
                        isSearchResult && `${SEARCH_HIGHLIGHT_CLASS} ring-2 ring-search`,
                    );

                    return (
                        <div
                            className="min-w-0"
                            key={label.id}
                            onClick={(event) => handleLabelSelection(event, label.id)}
                        >
                            <TagEditable
                                dataTagId={label.id}
                                className={tagBaseClassName}
                                isDisabled={!hasWritePermission}
                                value={label.name}
                                color={label.color}
                                size={size}
                                maxLength={LABEL_NAME_MAX_LENGTH}
                                onEditingChange={setIsEditingLabelName}
                                onCommit={(name) => handleLabelRename(label.id, name)}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default LabelElementPillsContainer;
